import { useState } from 'react'
import type { DocumentStatus } from '@project/contracts'
import { Button, Empty, Icon, Money, Panel, StatusMark } from '../design/index.js'
import type { DocumentDetail } from '../app/api.js'
import { formatDate, formatDateTime } from './format.js'

/**
 * What this document used to say (docs/02 §4, docs/06 §4).
 *
 * A purchase order that is amended keeps its number, so the version a supplier
 * accepted has to stay readable — otherwise "we ordered 40, you sent 60" has
 * no evidence on either side.
 *
 * Reference (PROJ1-019): every version as a card, newest first, the current
 * one included — "Revision 2" with the remark it was made for beneath it, its
 * status, and "<date> | Created by <user>" — down to "Original". The figures
 * of each version open under its card.
 *
 * A stored revision k is the version before revision k, kept when revision k
 * was made, with the remark, date and person of revision k. So version k's
 * own remark is on snapshot k, and its figures are on snapshot k + 1 (or are
 * the document's own, for the current version).
 */

interface Version {
  readonly key: string
  readonly title: string
  readonly remark: string | null
  readonly status: DocumentStatus
  readonly when: string | null
  readonly by: string | null
  readonly lineCount: number
  readonly total: string
}

const versionsOf = (doc: DocumentDetail): Version[] => {
  const byNo = new Map(doc.revisions.map((r) => [r.revisionNo, r]))
  const out: Version[] = []
  for (let k = doc.revisionNo; k >= 0; k -= 1) {
    const made = k > 0 ? byNo.get(k) : undefined
    const after = byNo.get(k + 1)
    const current = k === doc.revisionNo
    out.push({
      key: String(k),
      title: k === 0 ? 'Original' : `Revision ${k}`,
      remark: made?.reason ?? null,
      status: current ? doc.documentStatus : (after?.documentStatus ?? 'APPROVED'),
      when: made ? (made.revisionDate ? formatDate(made.revisionDate) : formatDateTime(made.createdAt)) : null,
      by: made?.createdBy ?? null,
      lineCount: current ? doc.lines.length : (after?.lineCount ?? 0),
      total: current ? doc.total : (after?.total ?? '0'),
    })
  }
  return out
}

const VersionCard = ({ version }: { version: Version }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="revision-card" style={{ border: '1px solid var(--line)', borderRadius: 12, padding: '14px 18px' }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
        <div>
          <div style={{ font: '600 16px/1.4 var(--font-body)', color: 'var(--ink)' }}>{version.title}</div>
          {version.remark ? <div className="muted" style={{ marginTop: 2 }}>{version.remark}</div> : null}
          <div style={{ marginTop: 8 }}><StatusMark status={version.status} /></div>
        </div>
        <div className="muted" style={{ textAlign: 'right' }}>
          {[version.when, version.by ? `Created by ${version.by}` : null].filter(Boolean).join(' | ')}
          <div>
            <button type="button" className="link-button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
              {open ? 'Hide figures' : 'Figures'}
            </button>
          </div>
        </div>
      </div>
      {open ? (
        <div className="row" style={{ gap: 24, marginTop: 10 }}>
          <span>{version.lineCount} line{version.lineCount === 1 ? '' : 's'}</span>
          <span>Value <Money value={version.total} /></span>
        </div>
      ) : null}
    </div>
  )
}

export const RevisionsTab = ({
  doc, docLabel, onRevise,
}: {
  doc: DocumentDetail
  docLabel?: string
  /** Offered only while the document is approved and the person may reopen it. */
  onRevise?: () => void
}) => {
  const label = docLabel ?? 'document'
  return (
    <Panel
      title="Revisions"
      aside={onRevise ? (
        <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={onRevise}>
          {/* "Revise the Project", as the reference words it. */}
          Revise the {label.charAt(0).toUpperCase()}{label.slice(1)}
        </Button>
      ) : undefined}
    >
      {doc.revisionNo === 0 ? (
        <Empty
          title="No revisions"
          body={[
            doc.documentStatus === 'APPROVED'
              ? 'This document has not been reopened since it was approved.'
              : 'A revision is kept each time an approved document is reopened for changes.',
            'The version on screen is always the current one.',
          ]}
        />
      ) : (
        <div className="stack" style={{ display: 'grid', gap: 12 }}>
          {versionsOf(doc).map((v) => <VersionCard key={v.key} version={v} />)}
        </div>
      )}
    </Panel>
  )
}
